import { cva, type VariantProps } from "class-variance-authority";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export const statusPillVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap",
  {
    variants: {
      tone: {
        neutral: "border-border bg-muted text-muted-foreground",
        brand: "border-primary/20 bg-primary/10 text-accent-foreground dark:text-primary",
        /** Contract tone "accent": a distinct violet-pink so it reads apart from brand/info. */
        accent: "border-chart-4/30 bg-chart-4/10 text-[oklch(0.48_0.17_330)] dark:text-chart-4",
        info: "border-info/25 bg-info/10 text-info",
        success: "border-success/25 bg-success/10 text-success",
        warning: "border-warning/30 bg-warning/12 text-warning",
        danger: "border-destructive/25 bg-destructive/10 text-destructive",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

export type StatusTone = NonNullable<VariantProps<typeof statusPillVariants>["tone"]>;

const dotTone: Record<StatusTone, string> = {
  neutral: "bg-muted-foreground/70",
  brand: "bg-primary",
  accent: "bg-chart-4",
  info: "bg-info",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-destructive",
};

interface StatusPillProps {
  tone?: StatusTone;
  children: ReactNode;
  dot?: boolean;
  className?: string;
}

export function StatusPill({ tone = "neutral", dot = true, children, className }: StatusPillProps) {
  return (
    <span className={cn(statusPillVariants({ tone }), className)}>
      {dot && <span aria-hidden="true" className={cn("size-1.5 rounded-full", dotTone[tone])} />}
      {children}
    </span>
  );
}

/**
 * Default status → tone mapping for the string enums the API returns
 * (e.g. `in_progress`). Entity screens can pass their own map.
 */
const DEFAULT_TONES: Record<string, StatusTone> = {
  draft: "neutral",
  new: "info",
  open: "info",
  not_started: "neutral",
  in_progress: "brand",
  active: "brand",
  pending: "warning",
  pending_approval: "warning",
  awaiting_client: "warning",
  on_hold: "warning",
  overdue: "danger",
  blocked: "danger",
  rejected: "danger",
  cancelled: "neutral",
  approved: "success",
  issued: "info",
  paid: "success",
  resolved: "success",
  completed: "success",
  closed: "neutral",
  won: "success",
  lost: "danger",
};

export function statusLabel(value: string): string {
  const s = value.replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Pill for an API status enum value, e.g. <EnumStatus value="in_progress" />. */
export function EnumStatus({ value, tones }: { value: string; tones?: Record<string, StatusTone> }) {
  const tone = tones?.[value] ?? DEFAULT_TONES[value] ?? "neutral";
  return <StatusPill tone={tone}>{statusLabel(value)}</StatusPill>;
}
